import { useEffect, useRef, useState } from 'react';
import { MapPin, ChevronDown, ChevronRight, Upload, Loader2 } from 'lucide-react';
import { api } from '../api';
import { useAuth } from '../lib/contextoAuth';
import SelectorEstado from './SelectorEstado';
import SelectorMunicipio from './SelectorMunicipio';
import CampoCodigoPostal from './CampoCodigoPostal';
import { estados as cargarEstados, municipios as cargarMunicipios,
         estadoDelCatalogo, olvidarCatalogo,
         indice, reconocer } from './geografia';

/* =====================================================================
   LOS LUGARES QUE TEGO CONOCE
   =====================================================================
   PARA QUE SIRVE, QUE NO ES DECORACION. Contesta una pregunta que hoy no
   tiene respuesta en ninguna pantalla: **«¿voy a poder elegir mi
   municipio de una lista, o lo voy a teclear?»**

   Importa porque la respuesta cambia según el estado y según si alguien
   ya cargó el catálogo oficial. Sin esta sección, la única forma de
   averiguarlo es abrir la ficha de una sucursal y mirar si el
   desplegable trae algo — o sea, descubrirlo a mitad de otra tarea.

   Y ES EL SITIO DONDE VIVIRAN LOS DOS SELECTORES MIENTRAS NADIE LOS
   CABLEA. Nacen aquí, en el carril que los escribe, y las pantallas de
   clientes, proveedores y sucursales los montan cuando sus dueños
   quieran: hasta entonces existen, se ven y se prueban en su casa, en
   vez de esperar apagados a que alguien se acuerde.
   ===================================================================== */
export default function CatalogoDeLugares() {
  const [abierto, setAbierto] = useState(false);
  const [estado, setEstado] = useState('');
  const [municipio, setMunicipio] = useState('');
  const [ficha, setFicha] = useState(null);
  const [cuantos, setCuantos] = useState(null);
  const [cp, setCp] = useState('');
  const [carga, setCarga] = useState(null);
  const [disponible, setDisponible] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [aviso, setAviso] = useState('');
  const [error, setError] = useState('');
  const entrada = useRef(null);
  const { tieneNivel } = useAuth();
  const esSupremo = tieneNivel('DUENO');

  useEffect(() => {
    let vivo = true;
    if (!abierto) return undefined;
    estadoDelCatalogo()
      .then((r) => { if (vivo) setCarga(r); })
      .catch(() => { if (vivo) setCarga(null); });
    api.get('/geografia/catalogo/disponible')
      .then((r) => { if (vivo) setDisponible(r); })
      .catch(() => { if (vivo) setDisponible(null); });
    return () => { vivo = false; };
  }, [abierto, aviso]);

  /* =====================================================================
     CARGAR EL CATALOGO · decision del dueño, 4 de septiembre de 2026
     =====================================================================
     Sin archivo carga el que trae esta version de TEGO, que es el caso
     normal despues de actualizar. Con archivo carga el que se sube, que
     es el caso de quien no tiene internet y lo trae en una memoria.

     TARDA: son ciento cuarenta y cinco mil renglones. El boton se apaga
     y lo dice, que es lo que pide la doctrina para toda operacion de mas
     de un instante. */
  async function cargar(archivo) {
    setCargando(true);
    setError('');
    setAviso('');
    try {
      /* `enviarFormulario` y no `subir`, porque SIN archivo hay que
         mandar un multipart vacío: cargar el de esta versión no lleva
         ninguno, y `subir` siempre añade un campo. */
      const cuerpo = new FormData();
      if (archivo) cuerpo.append('archivo', archivo);
      const r = await api.enviarFormulario('/geografia/catalogo', cuerpo);
      olvidarCatalogo();
      setAviso(r.mensaje);
    } catch (e) {
      setError(e.message);
    } finally {
      setCargando(false);
      if (entrada.current) entrada.current.value = '';
    }
  }

  useEffect(() => {
    let vivo = true;
    if (!abierto || !estado) return undefined;
    cargarEstados()
      .then((filas) => {
        const clave = reconocer(estado, indice(filas));
        const fila = filas.find((f) => f.clave === clave) ?? null;
        if (vivo) setFicha(fila);
        return clave ? cargarMunicipios(clave) : null;
      })
      .then((r) => {
        if (vivo) setCuantos(r ? { n: r.municipios.length, cargado: r.cargado } : null);
      })
      .catch(() => { if (vivo) { setFicha(null); setCuantos(null); } });
    return () => { vivo = false; };
  }, [abierto, estado]);

  return (
    <section className="rounded-xl border border-gray-200">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        aria-expanded={abierto}
        className="flex w-full items-center gap-2 px-3 py-3 text-left
                   font-semibold text-gray-900"
      >
        {abierto
          ? <ChevronDown className="h-4 w-4" aria-hidden="true" />
          : <ChevronRight className="h-4 w-4" aria-hidden="true" />}
        <MapPin className="h-4 w-4" aria-hidden="true" />
        Los lugares que TEGO conoce
      </button>

      {abierto && (
        <div className="flex flex-col gap-4 border-t border-gray-200 p-3">
          <p className="text-sm text-gray-600">
            Los 32 estados vienen puestos. Los municipios se cargan del catálogo
            oficial: elige un estado para ver si el suyo ya está.
          </p>

          <div className="grid gap-3 sm:grid-cols-2">
            <SelectorEstado
              id="lugares-estado"
              valor={estado}
              onChange={(nombre) => { setEstado(nombre); setMunicipio(''); }}
            />
            <SelectorMunicipio
              id="lugares-municipio"
              estado={estado}
              valor={municipio}
              onChange={(nombre) => setMunicipio(nombre)}
            />
          </div>

          {ficha && (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-4">
              <dt className="text-gray-500">Clave INEGI</dt>
              <dd className="font-mono text-gray-900">{ficha.clave}</dd>
              <dt className="text-gray-500">Clave para facturar</dt>
              <dd className="font-mono text-gray-900">{ficha.clave_sat}</dd>
            </dl>
          )}

          {/* ESTADO VACIO UTIL: qué es, por qué está vacío y qué pasa
              mientras tanto. Ninguna pantalla en blanco. */}
          {estado && cuantos && !cuantos.cargado && (
            <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
              El catálogo de municipios no está cargado. Mientras tanto
              el municipio se escribe a mano, como hasta ahora, y se guarda
              igual.
            </p>
          )}

          {estado && cuantos?.cargado && (
            <p className="text-sm text-gray-600">
              {cuantos.n} municipios cargados para este estado.
            </p>
          )}

          {/* CAPA 2 · probar un código postal aquí mismo. Es la forma
              más rápida de contestar «¿va a funcionar en mi zona?» sin
              abrir la ficha de un cliente para averiguarlo. */}
          <div className="border-t border-gray-200 pt-3">
            <div className="max-w-xs">
              <CampoCodigoPostal
                id="lugares-cp"
                etiqueta="Prueba un código postal"
                valor={cp}
                onChange={setCp}
              />
            </div>
          </div>

          {/* DE QUE ARCHIVO SALIO EL CATALOGO. Dentro de dos años, cuando
              una dirección no cuadre, la pregunta va a ser «¿de cuándo es
              esto?». */}
          <p className="text-xs text-gray-500">
            {carga?.cargado
              ? `Catálogo de lugares: ${carga.municipios} municipios y `
                + `${carga.asentamientos} colonias, de «${carga.nombre_archivo}»`
                + `${carga.fecha_corte ? `, con corte del ${carga.fecha_corte}` : ''}.`
              : 'El catálogo de códigos postales todavía no se ha cargado en '
                + 'este servidor. Mientras tanto, el municipio y el código '
                + 'postal se escriben a mano y se guardan igual.'}
          </p>

          {/* HAY UNO MÁS NUEVO DENTRO DE ESTA VERSIÓN. Se dice aquí en
              vez de dejar a alguien comparando fechas a mano. */}
          {disponible?.hay && !disponible.ya_cargado && (
            <p className="rounded-lg bg-[var(--color-marca-suave)] p-3 text-sm">
              Esta versión de TEGO trae un catálogo con corte del{' '}
              <strong>{disponible.fecha_corte}</strong>, sin cargar.
            </p>
          )}

          {aviso && (
            <p className="rounded-lg bg-green-50 p-3 text-sm text-green-800">
              {aviso}
            </p>
          )}
          {error && (
            <p className="rounded-lg bg-red-50 p-3 text-sm text-red-800">
              {error}
            </p>
          )}

          {/* SOLO EL ADMIN SUPREMO. El catálogo es uno para todo el
              servidor, así que no es una decisión de cualquiera. El botón
              no se pinta para los demás en vez de pintarlo y regañar. */}
          {esSupremo && (
            <div className="flex flex-wrap items-center gap-3 border-t
                            border-gray-200 pt-3">
              {disponible?.hay && !disponible.ya_cargado && (
                <button
                  type="button" onClick={() => cargar(null)} disabled={cargando}
                  className="inline-flex items-center gap-2 rounded-xl
                             bg-[var(--color-marca)] px-4 py-2 font-semibold
                             text-white disabled:bg-gray-300">
                  {cargando
                    ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    : <Upload className="h-4 w-4" aria-hidden="true" />}
                  {cargando ? 'Cargando…' : 'Cargar el de esta versión'}
                </button>
              )}
              <button
                type="button" onClick={() => entrada.current?.click()}
                disabled={cargando}
                className="inline-flex items-center gap-2 text-sm font-semibold
                           text-[var(--color-marca)] disabled:text-gray-400">
                <Upload className="h-4 w-4" aria-hidden="true" />
                Subir un archivo
              </button>
              <input
                ref={entrada} type="file" className="hidden" accept=".txt"
                onChange={(e) => e.target.files?.[0] && cargar(e.target.files[0])} />
              <span className="text-xs text-gray-500">
                «CPdescarga.txt», del sitio de Correos de México. Son 145 000
                renglones: tarda un poco.
              </span>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
